// Copyright ©️ 2026 eVolve MEP, LLC

import type { BendData, BendInputKey, YBCString } from '@evolvemep/api-ts';
import * as THREE from 'three';

import { isNil } from 'helpers/isNotNil';
import {
  type BendDef,
  buildBendDataFromBendDef,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/buildBendDataFromBendDef';
import { disposeGroup } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import type { DimensionLabel } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/DimensionLabel';
import { makeLabelFromLabelOpt } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/makeLabelFromLabelOpt';

/** Default conduit diameter for the bend shape preview (1.5 inch in feet). */
const DEFAULT_CONDUIT_SIZE = 0.125;

/**
 * The full set of inputs required to render a bend preview. Contains every possible bend
 * dimension key (angles, lengths, heights, etc.) as well as the physical pipe properties.
 *
 * Each `BendPreviewGeometry` subclass destructures only the keys it needs from this type.
 */
export type BendPreviewSpecifications = {
  /** measured in decimal feet */
  conduitSize: number;
  /** measured in decimal feet */
  bendRadius: number;
  ybcString?: YBCString;
  layoutDimensions?: {
    overallLength: number;
    marks: BendData['layoutDimensions'];
  };
} & Record<BendInputKey, number>;

export abstract class BendPreviewGeometry {
  /** Contains the pipe mesh(es) for the bend. Used by the renderer for bounding-box calculations. */
  readonly bendGroup = new THREE.Group();
  /** Contains all dimension labels for the bend. */
  readonly labelGroup = new THREE.Group();
  /**
   * Root group added to the renderer's scene. Contains {@link bendGroup} and {@link labelGroup}.
   * Pass to {@link BendPreviewRenderer.setGeometry} — do not add to the scene directly.
   */
  readonly rootGroup = new THREE.Group();

  dimensionLabels: DimensionLabel[] = [];

  private readonly realMaterial: THREE.MeshPhongMaterial;
  private readonly layoutMaterial: THREE.MeshPhongMaterial;

  constructor() {
    this.rootGroup.add(this.bendGroup);
    this.rootGroup.add(this.labelGroup);

    this.realMaterial = new THREE.MeshPhongMaterial({
      color: 0xaaaaaa,
      specular: 0xeeeeee,
      shininess: 25,
      side: THREE.DoubleSide,
    });
    this.layoutMaterial = new THREE.MeshPhongMaterial({
      color: 0xe07020,
      specular: 0xffaa66,
      shininess: 15,
      side: THREE.DoubleSide,
    });
    this.dispose = this.dispose.bind(this);
  }

  /**
   * Centers the geometry so the bend sits at the world origin with its bottom at Y=0, then
   * returns the resulting world-space bounding box of {@link bendGroup}.
   *
   * Returns `null` if the bounding box is empty (geometry has not yet been built).
   *
   * Called by {@link BendPreviewRenderer.setGeometry}. Labels are intentionally
   * excluded from the box, so toggling them does not cause the camera to re-fit.
   */
  computeAndCenter(): THREE.Box3 | null {
    this.rootGroup.position.set(0, 0, 0);
    // updateWorldMatrix(true, true) walks up to the scene root first, then down through all
    // descendants, ensuring a correct world-space bounding box regardless of parent transforms.
    this.rootGroup.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(this.bendGroup, true);
    if (box.isEmpty()) return null;
    const center = box.getCenter(new THREE.Vector3());
    this.rootGroup.position.set(-center.x, -box.min.y, -center.z);
    this.rootGroup.updateWorldMatrix(true, true);
    return new THREE.Box3().setFromObject(this.bendGroup, true);
  }

  /**
   * Override in subclasses to provide sensible default values for each bend-specific dimension key.
   * These are used when `bendPreviewData` is null (bend type preview mode).
   * The base implementation provides reasonable mid-range values for all keys.
   */
  getDefaultDimensions(): BendPreviewSpecifications {
    return {
      /** Default conduit diameter for the bend shape preview (1.5 inch in feet). */
      conduitSize: DEFAULT_CONDUIT_SIZE,
      /** Default bend radius for the bend type preview (6 inches in feet). */
      bendRadius: 0.5,
      angle: 45,
      stubHeight: 4,
      legLength: 5,
      distanceToObstruction: 5,
      offsetHeight: 3,
      takeUpLength: 8,
      kickHeight: 3,
      centerToEnd1: 4,
      centerToEnd2: 4,
      saddleHeight: 2,
      distanceToCenterOfObstruction: 4,
      saddleLength: 3,
      noseLength: 4,
      tailLength: 4,
    };
  }

  /**
   * This must be overridden in subclasses to create the bend definitions.
   * @param dimensions The dimensions of the bend.
   */
  abstract createBendDefs(dimensions: BendPreviewSpecifications): readonly BendDef[];

  /**
   * Method to generate the bend preview geometry. Do not override.
   * @param dimensions The dimensions of the bend. Pass `null` to render a bend type preview using
   * sensible defaults (the pipe will render with a distinct appearance to indicate the dimensions
   * are not yet validated).
   */
  createBendPreview(dimensions: BendPreviewSpecifications | null) {
    const resolvedDimensions: BendPreviewSpecifications = dimensions ?? this.getDefaultDimensions();
    const radialSegments = 16;

    try {
      const bendDefs = this.createBendDefs(resolvedDimensions);
      const { path, labelOpts } = buildBendDataFromBendDef(resolvedDimensions, bendDefs);

      const showDimensionValues = !isNil(dimensions);
      labelOpts.forEach((labelOpt) => {
        this.dimensionLabels.push(makeLabelFromLabelOpt(labelOpt, this.labelGroup, showDimensionValues));
      });

      const SEGMENTS_PER_UNIT_LENGTH = 16;

      const geometry = new THREE.TubeGeometry(
        path,
        Math.ceil(path.getLength() * SEGMENTS_PER_UNIT_LENGTH),
        resolvedDimensions.conduitSize / 2,
        radialSegments,
        false,
      );

      const mesh = new THREE.Mesh(geometry, isNil(dimensions) ? this.layoutMaterial : this.realMaterial);
      this.bendGroup.add(mesh);
    } catch (error) {
      // On failure, log the error and do not add the mesh to the scene
      // oxlint-disable-next-line no-console
      console.error('Error creating bend preview geometry:', error);
    }
  }

  dispose() {
    // Either layoutMaterial or realMaterial will be used, but not both. Dispose both to be safe.
    // We'll have a double-dispose here, which is slightly compute-wasteful, but preferable to risking a memory leak.
    this.layoutMaterial.dispose();
    this.realMaterial.dispose();
    disposeGroup(this.bendGroup);
    this.dimensionLabels.forEach((label) => label.dispose());
    disposeGroup(this.labelGroup);
    this.dimensionLabels = [];
    this.bendGroup.clear();
    this.labelGroup.clear();
    this.rootGroup.clear();
    this.rootGroup.removeFromParent();
  }
}
